/**
 * Curated Heroicons map.
 *
 * Previously AppIcon did `import * as HeroIcons from '@heroicons/react/24/outline'`
 * (and the solid set), which bundles ALL ~600 icons into every page because the
 * dynamic `iconSet[name]` lookup defeats tree-shaking. This module imports only
 * the icons actually used across the site via named imports, so the bundler can
 * tree-shake everything else away.
 *
 * To add a new icon: add a named import below and include it in the map.
 * Unknown names fall back to a question-mark icon in AppIcon (unchanged behaviour).
 */
import {
  AcademicCapIcon,
  ArchiveBoxIcon,
  ArrowDownIcon,
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowUpOnSquareIcon,
  BanknotesIcon,
  Bars3Icon,
  BeakerIcon,
  BoltIcon,
  BookmarkSquareIcon,
  BriefcaseIcon,
  BuildingStorefrontIcon,
  CalendarIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  CloudIcon,
  CodeBracketIcon,
  Cog6ToothIcon,
  CommandLineIcon,
  CubeIcon,
  CubeTransparentIcon,
  CurrencyPoundIcon,
  DocumentCheckIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  FireIcon,
  FolderOpenIcon,
  GlobeAltIcon,
  GlobeEuropeAfricaIcon,
  HandRaisedIcon,
  HandThumbDownIcon,
  HandThumbUpIcon,
  HeartIcon,
  HomeIcon,
  InboxIcon,
  InformationCircleIcon,
  LifebuoyIcon,
  LightBulbIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  MapIcon,
  MapPinIcon,
  MicrophoneIcon,
  NewspaperIcon,
  PaintBrushIcon,
  PaperAirplaneIcon,
  PauseIcon,
  PhoneIcon,
  PhotoIcon,
  PlayCircleIcon,
  PlayIcon,
  QuestionMarkCircleIcon,
  ScaleIcon,
  ShieldCheckIcon,
  SparklesIcon,
  StarIcon,
  SunIcon,
  SwatchIcon,
  TagIcon,
  TrophyIcon,
  TruckIcon,
  UserGroupIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import {
  ArrowUpOnSquareIcon as ArrowUpOnSquareIconSolid,
  ChartBarIcon as ChartBarIconSolid,
  CheckBadgeIcon as CheckBadgeIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
} from '@heroicons/react/24/solid';

import type { ComponentType, SVGProps } from 'react';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export const outlineIcons: Record<string, IconComponent> = {
  AcademicCapIcon,
  ArchiveBoxIcon,
  ArrowDownIcon,
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowUpOnSquareIcon,
  BanknotesIcon,
  Bars3Icon,
  BeakerIcon,
  BoltIcon,
  BookmarkSquareIcon,
  BriefcaseIcon,
  BuildingStorefrontIcon,
  CalendarIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  CloudIcon,
  CodeBracketIcon,
  Cog6ToothIcon,
  CommandLineIcon,
  CubeIcon,
  CubeTransparentIcon,
  CurrencyPoundIcon,
  DocumentCheckIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  FireIcon,
  FolderOpenIcon,
  GlobeAltIcon,
  GlobeEuropeAfricaIcon,
  HandRaisedIcon,
  HandThumbDownIcon,
  HandThumbUpIcon,
  HeartIcon,
  HomeIcon,
  InboxIcon,
  InformationCircleIcon,
  LifebuoyIcon,
  LightBulbIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  MapIcon,
  MapPinIcon,
  MicrophoneIcon,
  NewspaperIcon,
  PaintBrushIcon,
  PaperAirplaneIcon,
  PauseIcon,
  PhoneIcon,
  PhotoIcon,
  PlayCircleIcon,
  PlayIcon,
  QuestionMarkCircleIcon,
  ScaleIcon,
  ShieldCheckIcon,
  SparklesIcon,
  StarIcon,
  SunIcon,
  SwatchIcon,
  TagIcon,
  TrophyIcon,
  TruckIcon,
  UserGroupIcon,
  XCircleIcon,
  XMarkIcon,
};

export const solidIcons: Record<string, IconComponent> = {
  ArrowUpOnSquareIcon: ArrowUpOnSquareIconSolid,
  ChartBarIcon: ChartBarIconSolid,
  CheckBadgeIcon: CheckBadgeIconSolid,
  DocumentTextIcon: DocumentTextIconSolid,
};

export { QuestionMarkCircleIcon };
